/**
 * SeedDrawerContent Component
 *
 * Routes and renders tab content for the SeedDrawer based on the active tab.
 * Handles different tab types and seed-type-specific content.
 *
 * This component acts as a content router that:
 * - Switches between different tab views based on activeTab value
 * - Conditionally renders seed-type-specific tabs (webapp overview/application)
 * - Manages loading states while data is being fetched
 * - Passes appropriate data to each tab component
 *
 * @component
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { Container } from '@/components/legacy-ui';
import { Skeleton } from '@/components/ui/skeleton';
import { useUpdateSeed } from '@/hooks/useSeeds';
import { NotesTab } from '@/sections/NotesTab';
import {
  ApplicationTab,
  DetailsTab,
  DomainHistoryTab,
  SeedHistoryTab,
} from '@/sections/seeds/components/drawer/tabs';
import type { WhoisHistoryStatus } from '@/sections/seeds/hooks/drawer/useSeedDrawerData';
import { Access, Seed } from '@/types';

interface SeedDrawerContentProps {
  /** The seed to display content for */
  seed: Seed;
  /** Currently active tab identifier */
  activeTab: string;
  /** Formatted details content (WHOIS data or IP information) */
  displayDetails?: string;
  /** Array of historical WHOIS records for domain history tab */
  history?: unknown[];
  /** Information about how the seed was discovered/originated */
  originationInfo: string[];
  /** Access data for resolving user emails in history timeline */
  accesss: Access[];
  /** Loading status for details data */
  detailsStatus: 'error' | 'success' | 'pending';
  /** Loading status for history data */
  historyStatus: 'error' | 'success' | 'pending';
  /** WHOIS history file status from structured envelope */
  whoisHistoryStatus?: WhoisHistoryStatus;
  /** Domain has no registration at its registry (definitive backend signal) */
  isDomainUnregistered?: boolean;
  /** Callback to open comment modal for adding notes (omit to hide Add Note button) */
  onCommentClick?: () => void;
}

/**
 * SeedDrawerContent routes to the appropriate tab content
 *
 * Renders different tab components based on the activeTab value:
 *
 * Web application seeds:
 * - 'overview': Shows seed details and origination info
 * - 'application': Shows webapp sitemap/application content
 * - 'vulnerabilities': Shows vulnerability association notice
 * - 'seedHistory': Shows seed lifecycle timeline
 * - 'seedNotes': Shows notes associated with the seed
 *
 * Domain / IP seeds:
 * - 'details': Shows WHOIS/domain details and origination info
 * - 'domainHistory': Shows historical WHOIS data changes (domain only)
 * - 'seedHistory': Shows seed lifecycle timeline
 * - 'seedNotes': Shows notes associated with the seed
 *
 * Legacy tab names ('authentication', 'sitemap') are mapped to their
 * renamed equivalents ('overview', 'application') for stale URL compatibility.
 *
 * Loading states are managed globally and applied to all tabs except notes.
 *
 * @param seed - The seed object containing all metadata
 * @param activeTab - The currently selected tab identifier
 * @param displayDetails - Formatted details string for details tab
 * @param history - WHOIS history records for domain history tab
 * @param originationInfo - Discovery source information
 * @param accesss - Access objects for user email resolution
 * @param detailsStatus - Loading state for details data
 * @param historyStatus - Loading state for history data
 * @param onCommentClick - Handler for opening the comment modal
 */
export function SeedDrawerContent({
  seed,
  activeTab,
  displayDetails,
  history,
  originationInfo,
  accesss,
  detailsStatus,
  historyStatus,
  whoisHistoryStatus,
  isDomainUnregistered,
  onCommentClick,
}: SeedDrawerContentProps) {
  const { mutateAsync: updateSeed } = useUpdateSeed();

  // Handler for removing notes from seeds (uses different mutation than other entity types)
  const handleRemoveNote = useCallback(
    async (index: number) => {
      await updateSeed({
        type: seed.class === 'webapplication' ? 'webapplication' : 'asset',
        key: seed.key,
        identifier: seed.identifier,
        remove: index,
        showSnackbar: false,
      });
      toast.success('Note removed successfully');
    },
    [updateSeed, seed.class, seed.key, seed.identifier]
  );

  // Render tab content based on activeTab
  const renderTabContent = () => {
    switch (activeTab) {
      // 'authentication' is the legacy name for 'overview' (stale URL compat)
      case 'authentication':
      case 'overview':
      case 'details':
        return (
          <DetailsTab
            seed={seed}
            displayDetails={displayDetails}
            originationInfo={originationInfo}
            isDomainUnregistered={isDomainUnregistered}
          />
        );

      // 'sitemap' is the legacy name for 'application' (stale URL compat)
      case 'sitemap':
      case 'application':
        // Application view: left sub-nav switching Sitemap ⇄ REST API Endpoints
        // (both reused from the asset section, fed the seed's fields).
        return <ApplicationTab seed={seed} />;

      case 'vulnerabilities':
        return (
          <div className="text-chariot-text-secondary flex flex-col items-center justify-center gap-2 p-8 text-center">
            <span className="text-sm">
              No vulnerabilities linked to this seed.
            </span>
            <span className="text-xs">
              Vulnerabilities are associated with discovered assets, not seeds
              directly.
            </span>
          </div>
        );

      case 'domainHistory':
        return (
          <DomainHistoryTab
            seed={seed}
            history={history}
            whoisHistoryStatus={whoisHistoryStatus}
            isDomainUnregistered={isDomainUnregistered}
          />
        );

      case 'seedHistory':
        return <SeedHistoryTab seed={seed} accesss={accesss} />;

      case 'seedNotes':
        return (
          <NotesTab
            resource={seed}
            resourceType="Seed"
            accesss={accesss}
            onRemoveNote={onCommentClick ? handleRemoveNote : undefined}
            onOpenCommentModal={onCommentClick}
          />
        );

      // Unknown / removed / renamed tab (e.g. a stale `?seedDrawerTab=discovery`
      // bookmark after the drawer Discovery tab was removed) degrades to the
      // universal default CONTENT — the same DetailsTab the tab STRIP falls back
      // to via `tabs[0]` (SeedDrawerTabs). This keeps strip and content
      // consistent instead of showing a "Tab not found" error next to a
      // highlighted first tab. Mirrors the legacy-name mapping above.
      default:
        return (
          <DetailsTab
            seed={seed}
            displayDetails={displayDetails}
            originationInfo={originationInfo}
            isDomainUnregistered={isDomainUnregistered}
          />
        );
    }
  };

  return (
    <Container
      // The Application view (sub-nav + reused asset tables) is full-bleed so
      // its tables reach the panel edges. Container bakes in `p-6`, so override
      // it with `p-0` (twMerge wins on the later class); other tabs keep `p-6`.
      className={
        activeTab === 'application' || activeTab === 'sitemap' ? 'p-0' : 'p-6'
      }
      tabIndex={-1}
      id={activeTab}
    >
      {detailsStatus === 'pending' || historyStatus === 'pending' ? (
        <Skeleton shape="block" className="m-6 min-h-48 w-[calc(100%-48px)]" />
      ) : (
        renderTabContent()
      )}
    </Container>
  );
}
